import apiClient from './apiClient';

export interface PresignedUrlResponse {
  uploadUrl: string;
  s3Key: string;
  uploadId: string;
}

export const getPresignedUrl = async (
  fileName: string,
  uploadType: string,
  uploadUser: string
): Promise<PresignedUrlResponse> => {
  const response = await apiClient.post(`/upload/presigned-url/${uploadType}`, {
    fileName,
    uploadUser,
  });
  return response.data.data;
};

/**
 * S3 presigned URL のホワイトリスト検証
 * SSRF 防止: プロトコル・ホスト名 + バケット名を厳密にチェック
 *
 * 対応形式:
 *   - Virtual-hosted style: https://<bucket>.s3[.<region>].amazonaws.com/<key>
 *   - Path style:           https://s3[.<region>].amazonaws.com/<bucket>/<key>
 *
 * いずれもバケット名は "storagestack" プレフィックス必須（CDK 命名規則）
 */
const BUCKET_NAME_PATTERN = /^storagestack[a-z0-9-]*$/;
// Virtual-hosted: bucket.s3[.region].amazonaws.com
const VIRTUAL_HOSTED_PATTERN = /^(storagestack[a-z0-9-]*)\.s3(\.[a-z0-9-]+)?\.amazonaws\.com$/;
// Path style: s3[.region].amazonaws.com (バケットはパスから抽出)
const PATH_STYLE_HOST_PATTERN = /^s3(\.[a-z0-9-]+)?\.amazonaws\.com$/;

const validateS3Url = (url: string): boolean => {
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== 'https:') return false;

    // Virtual-hosted style: ホスト名にバケット名が含まれる
    if (VIRTUAL_HOSTED_PATTERN.test(parsed.hostname)) {
      return true;
    }

    // Path style: ホスト名は s3.region.amazonaws.com、バケットはパス先頭
    if (PATH_STYLE_HOST_PATTERN.test(parsed.hostname)) {
      const firstPathSegment = parsed.pathname.split('/').filter(Boolean)[0];
      return !!firstPathSegment && BUCKET_NAME_PATTERN.test(firstPathSegment);
    }

    return false;
  } catch {
    return false;
  }
};

export const uploadToS3 = async (
  presignedUrl: string,
  file: File
): Promise<void> => {
  if (!validateS3Url(presignedUrl)) {
    throw new Error('Invalid upload URL');
  }
  const safeUrl = new URL(presignedUrl).href;
  // SSRF safe:
  //   1. ブラウザ実行 (Node.js サーバーではない) — SSRF はサーバーサイドの脆弱性
  //   2. URL は validateS3Url() で S3 ホスト名ホワイトリスト + https プロトコルを検証済み
  //   3. URL.href で再シリアライズ済み（リダイレクト等の URL トリック対策）
  // nosemgrep: nodejs_scan.javascript-ssrf-rule-node_ssrf
  await fetch(safeUrl, {
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': 'text/csv',
    },
  });
};

export const deleteUploadRecord = async (uploadId: string): Promise<void> => {
  await apiClient.delete(`/upload/record/${uploadId}`);
};

export interface UploadRecord {
  id: string;
  file_name: string;
  upload_type: string;
  upload_user: string;
  s3_key: string;
  status: 'PENDING' | 'PROCESSING' | 'SUCCESS' | 'PARTIAL_SUCCESS' | 'FAILED';
  created_at: string;
  updated_at: string;
  success_count?: number;
  failed_count?: number;
  error_message?: string;
}

export interface ListUploadsResponse {
  items: UploadRecord[];
  count: number;
  next_token: string | null;
}

export const listUploads = async (params: {
  upload_type: string;
  from_date?: string;
  to_date?: string;
  limit?: number;
  next_token?: string;
}): Promise<ListUploadsResponse> => {
  const response = await apiClient.get('/upload/list', { params });
  return response.data.data;
};

